import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, ButtonBase, Typography } from '@mui/material';
import {
    Store as StoreIcon,
    VideoCall as VideoCallIcon,
} from '@mui/icons-material';

import videocallshopApi from '../../apis/videocallshop';
import ProductCard from '../storefront/ProductCard';
import { COLORS, FONT_TITLE } from '../storefront/storefrontStyles';

// Las secciones del home v33 (las mismas que la app): chips de categoría que
// filtran todo, tiendas abiertas ahora, un bloque por categoría con sus
// tiendas y productos, y todas las tiendas. Las categorías son las de la
// tienda: los productos no tienen (GET /products/by-category).

const PER_CATEGORY = 12;

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// Bloques por categoría. Se piden una vez: no cambian minuto a minuto. Si
// falla, el home sigue sin ellos.
export const useCategoryBlocks = () => {
    const [blocks, setBlocks] = useState([]);

    useEffect(() => {
        let alive = true;
        videocallshopApi
            .get('/products/by-category', {
                params: { perCategory: PER_CATEGORY },
            })
            .then(({ data }) => alive && setBlocks(data.data || []))
            .catch(() => alive && setBlocks([]));
        return () => {
            alive = false;
        };
    }, []);

    return blocks;
};

// "Vivo primero, después abiertas, después por nombre."
export const byStatus = (a, b) =>
    Number(!!b.isLive) - Number(!!a.isLive) ||
    Number(b.isOpen) - Number(a.isOpen) ||
    a.name.localeCompare(b.name, 'es');

export const SectionHeader = ({
    title,
    sub,
    dot,
    action,
    onAction,
    actionTo,
    actionState,
}) => (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
        {dot && (
            <Box
                sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor: dot,
                    boxShadow: `0 0 0 4px ${dot}33`,
                    mt: 1,
                    flexShrink: 0,
                }}
            />
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
                sx={{
                    fontFamily: FONT_TITLE,
                    fontWeight: 700,
                    fontSize: { xs: '1.2rem', md: '1.4rem' },
                    color: COLORS.text,
                }}
            >
                {title}
            </Typography>
            {sub && (
                <Typography sx={{ color: COLORS.muted, fontSize: '0.85rem' }}>
                    {sub}
                </Typography>
            )}
        </Box>
        {action && (
            <ButtonBase
                component={actionTo ? RouterLink : 'button'}
                to={actionTo}
                state={actionState}
                onClick={onAction}
                sx={{
                    color: COLORS.accent,
                    fontWeight: 700,
                    fontSize: '0.9rem',
                    mt: 0.5,
                    flexShrink: 0,
                }}
            >
                {action} ›
            </ButtonBase>
        )}
    </Box>
);

export const Chip = ({ label, selected, onClick }) => (
    <ButtonBase
        onClick={onClick}
        aria-pressed={selected}
        sx={{
            flexShrink: 0,
            px: 2,
            py: 1,
            borderRadius: '22px',
            fontWeight: 700,
            fontSize: '0.88rem',
            whiteSpace: 'nowrap',
            color: selected ? 'white' : COLORS.text,
            backgroundColor: selected ? COLORS.accent : COLORS.card,
            border: `1px solid ${selected ? COLORS.accent : COLORS.border}`,
            transition: 'background 0.2s, border-color 0.2s',
            '&:hover': {
                borderColor: selected ? COLORS.accent : COLORS.borderHover,
            },
        }}
    >
        {label}
    </ButtonBase>
);

// "Todo" + una por categoría. `wrap`: en el panel de filtros van en varias
// filas en vez de en una fila que scrollea.
export const CategoryChips = ({
    categories,
    selected,
    onSelect,
    wrap = false,
}) => (
    <Box
        sx={{
            display: 'flex',
            gap: 1,
            flexWrap: wrap ? 'wrap' : 'nowrap',
            overflowX: wrap ? 'visible' : 'auto',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
        }}
    >
        <Chip
            label="Todo"
            selected={selected == null}
            onClick={() => onSelect(null)}
        />
        {categories.map((c) => (
            <Chip
                key={c.id}
                label={c.name}
                selected={selected === c.id}
                onClick={() => onSelect(c.id)}
            />
        ))}
    </Box>
);

const Rail = ({ children }) => (
    <Box
        sx={{
            display: 'flex',
            gap: 1.5,
            overflowX: 'auto',
            pb: 1,
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
            scrollSnapType: 'x proximity',
            '& > *': { scrollSnapAlign: 'start' },
        }}
    >
        {children}
    </Box>
);

const StoreLogo = ({ store, size }) => {
    const [failed, setFailed] = useState(false);
    return (
        <Box
            sx={{
                width: size,
                height: size,
                borderRadius: size > 30 ? '12px' : '50%',
                overflow: 'hidden',
                flexShrink: 0,
                backgroundColor: COLORS.surface,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            {store.profileImageUrl && !failed ? (
                <Box
                    component="img"
                    src={store.profileImageUrl}
                    alt=""
                    onError={() => setFailed(true)}
                    sx={{
                        width: size,
                        height: size,
                        objectFit: 'cover',
                        display: 'block',
                    }}
                />
            ) : (
                <StoreIcon
                    sx={{ fontSize: size * 0.5, color: COLORS.subtle }}
                />
            )}
        </Box>
    );
};

// Tile de "Tiendas abiertas ahora": logo centrado con su estado debajo,
// nombre, categoría y "Videollamar".
const OpenStoreTile = ({ store, onCall, onOpen }) => (
    <Box
        onClick={() => onOpen(store)}
        sx={{
            flexShrink: 0,
            width: 210,
            p: 2,
            borderRadius: '16px',
            backgroundColor: COLORS.card,
            border: `1px solid ${
                store.isLive ? 'rgba(255,77,109,0.6)' : COLORS.border
            }`,
            cursor: 'pointer',
            transition: 'border-color 0.2s, transform 0.2s',
            '&:hover': {
                borderColor: COLORS.borderHover,
                transform: 'translateY(-2px)',
            },
        }}
    >
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
            }}
        >
            <StoreLogo store={store} size={52} />
            <Box
                sx={{
                    px: 1,
                    py: 0.3,
                    borderRadius: '6px',
                    fontSize: 11,
                    fontWeight: 800,
                    letterSpacing: 0.5,
                    backgroundColor: store.isLive
                        ? '#FF4D6D'
                        : 'rgba(41,210,171,0.18)',
                    color: store.isLive ? 'white' : COLORS.open,
                }}
            >
                {store.isLive ? 'EN VIVO' : 'ABIERTO'}
            </Box>
        </Box>
        <Typography
            sx={{
                color: COLORS.text,
                fontWeight: 700,
                fontSize: '1rem',
                mt: 1.2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            }}
        >
            {store.name}
        </Typography>
        <Typography
            sx={{
                color: COLORS.muted,
                fontSize: 11,
                letterSpacing: 0.5,
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            }}
        >
            {store.categoryName || ' '}
        </Typography>
        <ButtonBase
            onClick={(e) => {
                e.stopPropagation();
                onCall(store.storeId);
            }}
            sx={{
                mt: 1.5,
                width: '100%',
                py: 1.1,
                borderRadius: '10px',
                gap: 0.8,
                fontWeight: 800,
                fontSize: '0.9rem',
                color: '#0F1B2D',
                backgroundColor: COLORS.call,
                '&:hover': { backgroundColor: COLORS.callHover },
            }}
        >
            <VideoCallIcon sx={{ fontSize: 18 }} />
            Videollamar
        </ButtonBase>
    </Box>
);

export const OpenStoresRail = ({ stores, onCall, onOpen }) => (
    <Rail>
        {stores.map((store) => (
            <OpenStoreTile
                key={store.storeId}
                store={store}
                onCall={onCall}
                onOpen={onOpen}
            />
        ))}
    </Rail>
);

// Un bloque de "productos por categoría": nombre, "N productos · M tiendas",
// "Ver todo", las tiendas (logo + nombre + punto de abierto) y sus productos.
export const CategoryBlock = ({ block, stores, onStore }) => (
    <Box
        sx={{
            p: { xs: 2, md: 2.5 },
            mb: 2,
            borderRadius: '18px',
            backgroundColor: COLORS.surface,
            border: `1px solid ${COLORS.border}`,
        }}
    >
        <SectionHeader
            title={block.name}
            sub={`${plural(
                block.productCount,
                'producto',
                'productos'
            )} · ${plural(stores.length, 'tienda', 'tiendas')}`}
            action="Ver todo"
            actionTo={`/categoria/${block.categoryId}`}
            actionState={{ name: block.name }}
        />
        {stores.length > 0 && (
            <Box sx={{ mb: 2 }}>
                <Rail>
                    {stores.map((store) => (
                        <ButtonBase
                            key={store.storeId}
                            onClick={() => onStore(store)}
                            sx={{
                                flexShrink: 0,
                                gap: 1,
                                pl: 0.5,
                                pr: 1.5,
                                py: 0.5,
                                borderRadius: '20px',
                                backgroundColor: COLORS.card,
                                border: `1px solid ${COLORS.border}`,
                                color: COLORS.text,
                                fontSize: '0.85rem',
                                fontWeight: 600,
                                maxWidth: 220,
                            }}
                        >
                            <StoreLogo store={store} size={26} />
                            <Box
                                component="span"
                                sx={{
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {store.name}
                            </Box>
                            <Box
                                sx={{
                                    width: 7,
                                    height: 7,
                                    borderRadius: '50%',
                                    flexShrink: 0,
                                    backgroundColor: store.isOpen
                                        ? COLORS.open
                                        : COLORS.subtle,
                                }}
                            />
                        </ButtonBase>
                    ))}
                </Rail>
            </Box>
        )}
        <Rail>
            {block.items.map((product) => (
                <Box
                    key={product.productId}
                    sx={{ flexShrink: 0, width: { xs: 150, md: 180 } }}
                >
                    <ProductCard product={product} store={product.store} />
                </Box>
            ))}
        </Rail>
    </Box>
);
